import type { EnterpriseCustomVo } from "@zhihaoscm/entity";
import Theme, { defaultTheme, theme } from "@/function/theme";
import { useUserStore } from "@/stores/user";

/**
 * 租户nav的类型
 */
export interface Nav {
    /**
     * 标题
     */
    title: string;
    /**
     * 路由路径
     */
    path: string;
}

/**
 * 租户数据映射表
 */
const tenantMap = {
    chainCloud: {
        layout: {
            navs: [
                {
                    title: "首页",
                    path: "/"
                },
                {
                    title: "砂石指数",
                    path: "/public/sandGravelIndex"
                },
                {
                    title: "运价指数",
                    path: "/public/freightIndex"
                },
                {
                    title: "砂石资讯",
                    path: "/public/information"
                },
                {
                    title: "交易大厅",
                    path: "/public/trade"
                },
                {
                    title: "查车",
                    get path() {
                        return useUserStore().token
                            ? "/logistics/truckLocation"
                            : "/login?redirect=/logistics/truckLocation";
                    }
                },
                {
                    title: "查船",
                    get path() {
                        return useUserStore().token
                            ? "/logistics/findLocation"
                            : "/login?redirect=/logistics/findLocation";
                    }
                },
                {
                    title: "找船",
                    get path() {
                        return useUserStore().token ? "/logistics/shipOrder" : "/login?redirect=/logistics/shipOrder";
                    }
                }
            ],
            footerNavs: [
                {
                    title: "关于链云",
                    list: [
                        {
                            title: "关于我们",
                            path: "/public/article/1"
                        },
                        {
                            title: "隐私政策",
                            path: "/public/article/3"
                        },
                        {
                            title: "用户协议",
                            path: "/public/article/2"
                        },
                        {
                            title: "留言反馈",
                            path: "/public/feedback"
                        },
                        {
                            title: "帮助中心",
                            path: "/public/helpCenter"
                        }
                    ]
                },
                {
                    title: "指数研究",
                    list: [
                        {
                            title: "砂石指数",
                            path: "/public/sandGravelIndex"
                        },
                        {
                            title: "运价指数",
                            path: "/public/freightIndex"
                        }
                    ]
                },
                {
                    title: "物流管理",
                    list: [
                        {
                            title: "查车",
                            get path() {
                                return useUserStore().token ? "/logistics/truckLocation" : "/login";
                            }
                        },
                        {
                            title: "查船",
                            get path() {
                                return useUserStore().token ? "/logistics/findLocation" : "/login";
                            }
                        },
                        {
                            title: "找船",
                            get path() {
                                return useUserStore().token ? "/logistics/shipOrder" : "/login";
                            }
                        }
                    ]
                },
                {
                    title: "资讯",
                    list: [
                        {
                            title: "砂石资讯",
                            path: "/public/information"
                        }
                    ]
                }
            ]
        }
    },
    other: {
        layout: {
            navs: [
                {
                    title: "首页",
                    path: "/"
                },
                {
                    title: "公司产品",
                    path: "/public/trade"
                },
                {
                    title: "公司项目",
                    path: "/public/project"
                },
                {
                    title: "公司公告",
                    path: `/public/announcements`
                },
                {
                    title: "公司资讯",
                    path: `/public/information`
                },
                {
                    title: "关于建业",
                    path: "/public/article/1"
                }
            ],
            footerNavs: [
                {
                    get title() {
                        return `关于${Theme.getInstance().systemAbbreviation}`;
                    },
                    list: [
                        {
                            title: "关于我们",
                            path: "/public/article/1"
                        },
                        {
                            title: "隐私政策",
                            path: "/public/article/3"
                        },
                        {
                            title: "用户协议",
                            path: "/public/article/2"
                        }
                    ]
                },
                {
                    title: "公司讯息",
                    list: [
                        {
                            title: "公司公告",
                            path: "/public/announcements"
                        },
                        {
                            title: "公司资讯",
                            path: "/public/information"
                        }
                    ]
                }
            ]
        }
    }
};

/**
 * 租户类
 */
export default class Tenant {
    /**
     * EnterpriseCustomVo, 用于存储后台配置的租户的数据
     */
    private _enterpriseCustomVo: EnterpriseCustomVo | undefined;

    /**
     * 租户Id
     */
    readonly tenantId: string = import.meta.env.VITE_TENANT_ID;

    /**
     * 主题
     */
    private _theme = theme;

    private constructor() {}

    /**
     * 请求接口, 初始化数据
     */
    public async init() {
        // 建业相关-目前用不上
        // if (this.tenantId) {
        //     try {
        //         const { enterpriseCustomService } = await import("@/api/enterpriseCustomService");
        //         this._enterpriseCustomVo = await enterpriseCustomService.findVo(+this.tenantId);
        //         this.handleEnterpriseCustomerVo();
        //         this.setThemeFromEnterpriseCustomVo(this.enterpriseCustomVo!);
        //     } catch (error) {
        //         console.log("tenant-init-error-", error);
        //     }
        // } else {
        //     this._theme.setTheme(defaultTheme);
        // }

        this._theme.setTheme(defaultTheme);
    }

    /**
     * 处理enterpriseCustomVo
     */
    private handleEnterpriseCustomerVo() {
        this._enterpriseCustomVo && (this._enterpriseCustomVo.enterpriseCustom.aiName = defaultTheme.aiName);
    }

    /**
     * 将EnterpriseCustomVo转为主题
     */
    setThemeFromEnterpriseCustomVo(enterpriseCustomVo: EnterpriseCustomVo) {
        const findFilePath = (id: number) => {
            return enterpriseCustomVo.dataMap[id]?.path;
        };
        this._theme.setTheme({
            systemName: enterpriseCustomVo.enterpriseCustom.systemName,
            systemAbbreviation: enterpriseCustomVo.enterpriseCustom.systemAbbreviation,
            systemHeaderSlogan: enterpriseCustomVo.enterpriseCustom.systemHeaderSlogan,
            aiName: enterpriseCustomVo.enterpriseCustom.aiName,
            serviceTel: enterpriseCustomVo.enterpriseCustom.serviceTel,
            copyright: enterpriseCustomVo.enterpriseCustom.copyright,
            icpFilings: enterpriseCustomVo.enterpriseCustom.icpFilings,
            internetFilings: enterpriseCustomVo.enterpriseCustom.internetFilings,
            themeColor: enterpriseCustomVo.enterpriseStyleCustom?.pcColor,
            loginLogo: findFilePath(enterpriseCustomVo.enterpriseStyleCustom?.pcLoginLogo),
            loginBgColor: findFilePath(enterpriseCustomVo.enterpriseStyleCustom?.pcLoginPic),
            publicHeaderLogo: findFilePath(enterpriseCustomVo.enterpriseStyleCustom?.pcPublicHeadLogo),
            publicBanner: findFilePath(enterpriseCustomVo.enterpriseStyleCustom?.pcPublicBackPic),
            AIImage: findFilePath(enterpriseCustomVo.enterpriseStyleCustom?.pcPublicAi),
            publicFooterLogo: findFilePath(enterpriseCustomVo.enterpriseStyleCustom?.pcPublicBottomLogo),
            consoleLogo: findFilePath(enterpriseCustomVo.enterpriseStyleCustom?.pcControlHeadLogo),
            accountAvatar: findFilePath(enterpriseCustomVo.enterpriseStyleCustom?.pcControlAccountPic),
            AIAvatar: findFilePath(enterpriseCustomVo.enterpriseStyleCustom?.pcPublicAiHead),
            buttonTextColor: ""
        });
    }

    /**
     * 获取enterpriseCustomVo
     */
    get enterpriseCustomVo() {
        return this._enterpriseCustomVo;
    }

    /**
     * 租户是否是链云
     */
    get isChainCloud() {
        return !this.tenantId;
    }

    /**
     * 获取租户布局数据
     */
    get layout() {
        if (this.isChainCloud) {
            return tenantMap.chainCloud.layout;
        } else {
            return tenantMap.other.layout;
        }
    }

    /**
     * 获取租户的主题
     */
    get theme() {
        return this._theme;
    }

    /**
     * 单例
     * @private
     */
    private static instance: Tenant;

    /**
     * 获取单例
     */
    static getInstance() {
        if (!Tenant.instance) {
            Tenant.instance = new Tenant();
        }
        return Tenant.instance;
    }
}
